import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  Loader2,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

type RequestStatus = 'form' | 'pending' | 'approved' | 'denied' | 'expired' | 'error';

interface LocationOption {
  id: string;
  name: string;
}

export default function RequestCount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('org') || '';

  const [status, setStatus] = useState<RequestStatus>('form');
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Form state
  const [organizationId, setOrganizationId] = useState(orgId);
  const [locationId, setLocationId] = useState('');
  const [cashierName, setCashierName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Polling state
  const [requestToken, setRequestToken] = useState('');
  const [locationName, setLocationName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [deniedReason, setDeniedReason] = useState('');

  // Fetch locations when org ID is entered
  const fetchLocations = useCallback(async (oId: string) => {
    if (!oId) {
      setLocations([]);
      return;
    }
    setLoadingLocations(true);
    try {
      const { data, error } = await supabase.rpc('get_org_locations_public', {
        p_organization_id: oId,
      });
      if (error) throw error;
      setLocations(data || []);
    } catch {
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    if (organizationId && organizationId.length >= 30) {
      fetchLocations(organizationId);
    }
  }, [organizationId, fetchLocations]);

  // Poll for approval
  useEffect(() => {
    if (status !== 'pending' || !requestToken) return;

    const interval = setInterval(async () => {
      try {
        const { data, error } = await supabase.rpc('check_count_request_status', {
          p_request_token: requestToken,
        });
        if (error) throw error;

        if (data.status === 'approved' && data.share_token) {
          setStatus('approved');
          clearInterval(interval);
          toast.success('Request approved! Redirecting to your count...');
          setTimeout(() => {
            navigate(`/cashier-count/${data.share_token}`);
          }, 2000);
        } else if (data.status === 'denied') {
          setStatus('denied');
          setDeniedReason(data.denied_reason || '');
          clearInterval(interval);
        } else if (data.status === 'expired') {
          setStatus('expired');
          clearInterval(interval);
        }
      } catch {
        // Silently continue polling
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [status, requestToken, navigate]);

  const handleSubmit = async () => {
    if (!organizationId || !locationId || !cashierName.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('request_cashier_count', {
        p_location_id: locationId,
        p_organization_id: organizationId,
        p_cashier_name: cashierName.trim(),
        p_notes: notes.trim() || null,
      });

      if (error) throw error;

      setRequestToken(data.request_token);
      setLocationName(data.location_name || '');
      setOrgName(data.organization_name || '');
      setStatus('pending');
      toast.success('Request submitted! Waiting for admin approval...');
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 mx-auto mb-4">
            <ClipboardList className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Request Stock Count</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Submit a request to start a cashier stock count. Your admin will be notified for approval.
          </p>
        </div>

        {/* Form */}
        {status === 'form' && (
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <div className="space-y-2">
              <Label>Organization ID</Label>
              <Input
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                placeholder="Provided by your admin"
                disabled={!!orgId}
              />
              <p className="text-xs text-muted-foreground">
                Ask your manager for the organization ID if you don't have it.
              </p>
            </div>

            {locations.length > 0 && (
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your location..." />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {loadingLocations && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading locations...
              </div>
            )}

            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input
                value={cashierName}
                onChange={(e) => setCashierName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Morning shift, Station 2"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !organizationId || !locationId || !cashierName.trim()}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Submit Request
            </Button>
          </div>
        )}

        {/* Pending State */}
        {status === 'pending' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 mx-auto">
              <Clock className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-pulse" />
            </div>
            <h2 className="text-lg font-semibold">Waiting for Approval</h2>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Your request has been sent to the admin for <strong>{orgName}</strong>.</p>
              <p>Location: <strong>{locationName}</strong></p>
              <p>Cashier: <strong>{cashierName}</strong></p>
            </div>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Checking every 10 seconds...
            </div>
            <p className="text-xs text-muted-foreground">
              Keep this page open. You will be automatically redirected once approved.
            </p>
          </div>
        )}

        {/* Approved State */}
        {status === 'approved' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold">Request Approved!</h2>
            <p className="text-sm text-muted-foreground">
              Redirecting you to your stock count...
            </p>
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" />
          </div>
        )}

        {/* Denied State */}
        {status === 'denied' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mx-auto">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold">Request Denied</h2>
            <p className="text-sm text-muted-foreground">
              Your admin has denied this count request.
            </p>
            {deniedReason && (
              <p className="text-sm text-muted-foreground">
                Reason: <em>{deniedReason}</em>
              </p>
            )}
            <Button onClick={() => setStatus('form')} variant="outline">
              Submit New Request
            </Button>
          </div>
        )}

        {/* Expired State */}
        {status === 'expired' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mx-auto">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold">Request Expired</h2>
            <p className="text-sm text-muted-foreground">
              This request was not reviewed within 24 hours. Please submit a new one.
            </p>
            <Button onClick={() => setStatus('form')} variant="outline">
              Submit New Request
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
